import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);

  // Handle scroll effect for navbar
  useState(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  });

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      question: "How do I set up GASOIL VPN?",
      answer: "Setting up GASOIL VPN is simple. Download the application, install it on your device, create an account, and connect with a single click. Our intuitive interface makes it easy for anyone to get started in minutes."
    },
    {
      question: "Is GASOIL VPN compatible with my devices?",
      answer: "Yes! GASOIL VPN works seamlessly across all major platforms including Windows, macOS, iOS, Android, and Linux. You can protect multiple devices simultaneously with a single account."
    },
    {
      question: "Will GASOIL VPN slow down my internet connection?",
      answer: "GASOIL VPN is optimized for speed. While encryption adds minimal overhead, our global server network and advanced protocols ensure you maintain fast, reliable connections for streaming, browsing, and downloading."
    },
    {
      question: "What security protocols does GASOIL VPN use?",
      answer: "We use military-grade AES-256 encryption combined with secure protocols including OpenVPN, IKEv2, and WireGuard. Our zero-log policy ensures your online activities remain completely private."
    },
    {
      question: "Can I use GASOIL VPN for streaming?",
      answer: "Absolutely! GASOIL VPN provides fast, stable connections perfect for streaming content from anywhere in the world. Our servers are optimized to deliver buffer-free streaming experiences."
    },
    {
      question: "What is your refund policy?",
      answer: "We stand behind our service with confidence. If you're not completely satisfied, contact our support team within 30 days for assistance. We're committed to ensuring you have the best VPN experience."
    },
    {
      question: "How can I contact technical support?",
      answer: "Our support team is available via email at help@gasoilltda.com or by phone at +1 6204496234. We're here to help you with any questions or technical issues you may encounter."
    },
    {
      question: "Does GASOIL VPN keep logs of my activity?",
      answer: "No. We maintain a strict zero-log policy. We do not track, collect, or store any information about your online activities, ensuring complete privacy and anonymity while using our service."
    }
  ];

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="https://public.readdy.ai/ai/img_res/f9cb9542-6fc6-425b-895d-bc5694ffe6bc.png" alt="GASOIL Logo" className="h-10 w-auto" />
            <span className={`text-2xl font-bold ${isScrolled ? 'text-gray-900' : 'text-gray-900'}`}>GASOIL</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#home" className={`text-sm font-medium hover:text-teal-600 transition-colors cursor-pointer ${isScrolled ? 'text-gray-700' : 'text-gray-700'}`}>Home</a>
            <a href="#about" className={`text-sm font-medium hover:text-teal-600 transition-colors cursor-pointer ${isScrolled ? 'text-gray-700' : 'text-gray-700'}`}>About</a>
            <a href="#vpn-service" className={`text-sm font-medium hover:text-teal-600 transition-colors cursor-pointer ${isScrolled ? 'text-gray-700' : 'text-gray-700'}`}>VPN Service</a>
            <a href="#features" className={`text-sm font-medium hover:text-teal-600 transition-colors cursor-pointer ${isScrolled ? 'text-gray-700' : 'text-gray-700'}`}>Features</a>
            <a href="#contact" className={`text-sm font-medium hover:text-teal-600 transition-colors cursor-pointer ${isScrolled ? 'text-gray-700' : 'text-gray-700'}`}>Contact</a>
            <a href="#faq" className={`text-sm font-medium hover:text-teal-600 transition-colors cursor-pointer ${isScrolled ? 'text-gray-700' : 'text-gray-700'}`}>FAQ</a>
          </div>
          <button className="md:hidden w-8 h-8 flex items-center justify-center">
            <i className="ri-menu-line text-2xl text-gray-900"></i>
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="pt-32 pb-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-6xl md:text-7xl font-bold text-gray-900 leading-tight mb-6">
            Secure Your Digital Life<br />
            <span className="text-teal-600">With GASOIL VPN</span>
          </h1>
          <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
            Professional VPN solution for complete online privacy and security
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="#contact" className="px-8 py-4 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap">
              Get Started
            </a>
            <a href="#about" className="px-8 py-4 bg-white text-gray-900 border-2 border-gray-900 rounded-lg font-medium hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
              Learn More
            </a>
          </div>
          <div className="mt-16">
            <img 
              src="https://public.readdy.ai/ai/img_res/4e1827c25ba219b657603563dda4e72f.jpg" 
              alt="VPN Network Visualization" 
              className="w-full h-auto rounded-2xl shadow-2xl"
            />
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="w-full h-96 rounded-[40px] overflow-hidden shadow-xl">
                <img 
                  src="https://readdy.ai/api/search-image?query=abstract%20geometric%20illustration%20representing%20VPN%20technology%20and%20network%20security%2C%20soft%20gradient%20background%20in%20blue-gray%20tones%2C%20modern%20minimalist%20design%20with%20interconnected%20shapes%20and%20lines%20symbolizing%20data%20protection%20and%20encrypted%20connections%2C%20professional%20tech%20aesthetic&width=600&height=600&seq=gasoil-about-tech&orientation=squarish" 
                  alt="VPN Technology" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div>
              <div className="inline-block px-4 py-2 bg-teal-100 text-teal-700 rounded-full text-sm font-medium mb-6">
                ABOUT GASOIL
              </div>
              <h2 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Protecting Your<br />
                Online Privacy
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                GASOIL is a leading VPN service provider dedicated to safeguarding your digital presence. Based in Colombia, we develop cutting-edge applications that prioritize user privacy and security in an increasingly connected world.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                Our mission is to provide individuals and businesses with reliable, fast, and secure VPN solutions that protect sensitive data, ensure anonymous browsing, and enable unrestricted access to the internet. With GASOIL VPN, your online activities remain private and protected from prying eyes.
              </p>
              <a href="#contact" className="inline-flex items-center gap-2 px-6 py-3 bg-gray-100 text-gray-900 rounded-lg font-medium hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap">
                Contact Us
                <i className="ri-arrow-right-up-line text-xl"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* VPN Service Section */}
      <section id="vpn-service" className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <p className="text-sm text-gray-500 uppercase tracking-wider mb-4">OUR SERVICES</p>
            <h2 className="text-5xl font-bold text-gray-900 leading-tight">
              Why Choose<br />
              GASOIL VPN
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-50 rounded-[20px] p-10 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-4xl text-gray-900"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Military-Grade Encryption</h3>
              <p className="text-gray-600 leading-relaxed">
                Your data is protected with AES-256 encryption, the same standard used by governments and security experts worldwide. Browse with confidence knowing your information is completely secure.
              </p>
            </div>
            <div className="bg-gray-50 rounded-[20px] p-10 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 flex items-center justify-center mb-6">
                <i className="ri-global-line text-4xl text-gray-900"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Global Server Network</h3>
              <p className="text-gray-600 leading-relaxed">
                Access our extensive network of high-speed servers located around the world. Enjoy fast, reliable connections no matter where you are or what you're doing online.
              </p>
            </div>
            <div className="bg-gray-50 rounded-[20px] p-10 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 flex items-center justify-center mb-6">
                <i className="ri-eye-off-line text-4xl text-gray-900"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Zero-Log Policy</h3>
              <p className="text-gray-600 leading-relaxed">
                We don't track, collect, or store any information about your online activities. Your privacy is our priority, and we're committed to keeping it that way with our strict no-logs policy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-6 bg-amber-50">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <p className="text-sm text-gray-500 uppercase tracking-wider mb-4 font-serif">FEATURES</p>
            <h2 className="text-5xl font-bold text-gray-900 leading-tight">
              Complete VPN<br />
              <span className="italic font-serif">Protection</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-teal-100 rounded-[30px] overflow-hidden hover:scale-105 transition-transform cursor-pointer">
              <div className="h-64 flex items-center justify-center p-8">
                <div className="w-20 h-20 flex items-center justify-center">
                  <i className="ri-speed-line text-6xl text-teal-700"></i>
                </div>
              </div>
              <div className="bg-white p-6 text-center">
                <h3 className="text-lg font-bold text-gray-900">Fast Connection</h3>
              </div>
            </div>
            <div className="bg-pink-100 rounded-[30px] overflow-hidden hover:scale-105 transition-transform cursor-pointer">
              <div className="h-64 flex items-center justify-center p-8">
                <div className="w-20 h-20 flex items-center justify-center">
                  <i className="ri-devices-line text-6xl text-pink-700"></i>
                </div>
              </div>
              <div className="bg-white p-6 text-center">
                <h3 className="text-lg font-bold text-gray-900">Multi-Device Support</h3>
              </div>
            </div>
            <div className="bg-orange-100 rounded-[30px] overflow-hidden hover:scale-105 transition-transform cursor-pointer">
              <div className="h-64 flex items-center justify-center p-8">
                <div className="w-20 h-20 flex items-center justify-center">
                  <i className="ri-shut-down-line text-6xl text-orange-700"></i>
                </div>
              </div>
              <div className="bg-white p-6 text-center">
                <h3 className="text-lg font-bold text-gray-900">Kill Switch</h3>
              </div>
            </div>
            <div className="bg-purple-100 rounded-[30px] overflow-hidden hover:scale-105 transition-transform cursor-pointer">
              <div className="h-64 flex items-center justify-center p-8">
                <div className="w-20 h-20 flex items-center justify-center">
                  <i className="ri-lock-password-line text-6xl text-purple-700"></i>
                </div>
              </div>
              <div className="bg-white p-6 text-center">
                <h3 className="text-lg font-bold text-gray-900">DNS Leak Protection</h3>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-0 rounded-3xl overflow-hidden shadow-2xl">
            <div className="bg-teal-700 p-16 flex flex-col justify-end relative overflow-hidden">
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-10 left-10 w-32 h-32 border-4 border-white rounded-full"></div>
                <div className="absolute bottom-20 right-10 w-24 h-24 border-4 border-white rounded-full"></div>
              </div>
              <div className="relative z-10">
                <p className="text-sm text-teal-200 uppercase tracking-wider mb-6">GET IN TOUCH</p>
                <h2 className="text-6xl font-bold text-white leading-tight">
                  Contact<br />
                  GASOIL<br />
                  Today
                </h2>
              </div>
            </div>
            <div className="bg-white p-16 flex flex-col justify-center">
              <h3 className="text-4xl font-bold text-gray-900 text-center mb-4 leading-tight">
                We're Here<br />
                To Help
              </h3>
              <p className="text-center text-gray-600 mb-8 leading-relaxed">
                Have questions about GASOIL VPN? Our support team is ready to assist you with any inquiries or technical support you may need.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-mail-line text-xl text-teal-600"></i>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Email</p>
                    <a href="mailto:help@gasoilltda.com" className="text-gray-900 font-medium hover:text-teal-600 transition-colors cursor-pointer">
                      help@gasoilltda.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-phone-line text-xl text-teal-600"></i>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Phone</p>
                    <a href="tel:+16204496234" className="text-gray-900 font-medium hover:text-teal-600 transition-colors cursor-pointer">
                      +1 6204496234
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-map-pin-line text-xl text-teal-600"></i>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Address</p>
                    <p className="text-gray-900 font-medium">
                      CARRERA 34 24A 14 16 18<br />
                      VILLAVICENCIO, Colombia 500001
                    </p>
                  </div>
                </div>
              </div>
              <a href="mailto:help@gasoilltda.com" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gray-900 text-white rounded-full font-medium hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap mx-auto">
                Send Message
                <i className="ri-arrow-right-line text-xl"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-5xl font-bold text-gray-900 text-center mb-16 font-serif">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-8 py-6 flex items-center justify-between text-left cursor-pointer"
                >
                  <span className="text-lg font-bold text-gray-900 pr-4">{faq.question}</span>
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <i className={`${openFaq === index ? 'ri-subtract-line' : 'ri-add-line'} text-2xl text-gray-900 transition-transform`}></i>
                  </div>
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openFaq === index ? 'max-h-96' : 'max-h-0'}`}>
                  <div className="px-8 pb-6">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-teal-900 text-white py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-16">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src="https://public.readdy.ai/ai/img_res/f9cb9542-6fc6-425b-895d-bc5694ffe6bc.png" alt="GASOIL Logo" className="h-10 w-auto" />
                <span className="text-2xl font-bold">GASOIL</span>
              </div>
              <p className="text-teal-200 leading-relaxed text-sm">
                Professional VPN solutions protecting your digital privacy and security worldwide.
              </p>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-wider mb-4 font-bold">NEWSLETTER</h4>
              <div className="flex items-center border-b border-teal-700 pb-2 mb-2">
                <input 
                  type="email" 
                  placeholder="Your email" 
                  className="bg-transparent flex-1 outline-none text-sm text-white placeholder-teal-300"
                />
                <button className="w-6 h-6 flex items-center justify-center cursor-pointer">
                  <i className="ri-arrow-right-line text-xl"></i>
                </button>
              </div>
              <p className="text-xs text-teal-300">
                Subscribe to our newsletter for updates
              </p>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-wider mb-4 font-bold">QUICK LINKS</h4>
              <div className="space-y-3">
                <a href="#home" className="block text-sm text-teal-100 hover:text-white transition-colors cursor-pointer">Home</a>
                <a href="#about" className="block text-sm text-teal-100 hover:text-white transition-colors cursor-pointer">About</a>
                <a href="#vpn-service" className="block text-sm text-teal-100 hover:text-white transition-colors cursor-pointer">VPN Service</a>
                <a href="#features" className="block text-sm text-teal-100 hover:text-white transition-colors cursor-pointer">Features</a>
                <a href="#contact" className="block text-sm text-teal-100 hover:text-white transition-colors cursor-pointer">Contact</a>
                <a href="#faq" className="block text-sm text-teal-100 hover:text-white transition-colors cursor-pointer">FAQ</a>
              </div>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-wider mb-4 font-bold">FOLLOW US</h4>
              <div className="space-y-3">
                <a href="#" className="flex items-center gap-3 text-sm text-teal-100 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-xl"></i>
                  <span>Facebook</span>
                </a>
                <a href="#" className="flex items-center gap-3 text-sm text-teal-100 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-twitter-x-fill text-xl"></i>
                  <span>Twitter</span>
                </a>
                <a href="#" className="flex items-center gap-3 text-sm text-teal-100 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-xl"></i>
                  <span>LinkedIn</span>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-teal-800 pt-12">
            <div className="text-center">
              <h2 className="text-7xl font-bold text-teal-800 mb-8">GASOIL</h2>
              <p className="text-sm text-teal-300">
                © 2025 GASOIL. All rights reserved. | <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">Powered by Readdy</a>
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
